import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
} from "recharts";
import { getDetailedClimateColor } from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";
import {
  OPENROUTER_API_KEY,
  API_BASE_URL,
  isConfigValid,
} from "../utils/config";

const WindAnalysis = ({ climateInfo, temperatureData }) => {
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [loadingAiInterpretation, setLoadingAiInterpretation] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [designSuggestions, setDesignSuggestions] = useState(null);
  const [loadingDesignSuggestions, setLoadingDesignSuggestions] =
    useState(false);
  const [designError, setDesignError] = useState(null);

  if (!climateInfo) {
    return null;
  }

  // Calculate wind performance metrics based on climate and temperature data
  const tempRange = parseFloat(climateInfo.tempRange || 0);
  const annualTemp = parseFloat(climateInfo.annualTemp || 0);

  // Estimate wind speeds based on climate characteristics
  const baseWindSpeed = 3 + tempRange / 5; // Base estimation
  const annualAvgWindSpeed = baseWindSpeed;

  // Estimate seasonal variations
  const winterWindSpeed =
    climateInfo.mainClass === "D" ? baseWindSpeed * 1.3 : baseWindSpeed * 0.9;
  const springWindSpeed = baseWindSpeed * 1.1;
  const summerWindSpeed =
    climateInfo.mainClass === "A" ? baseWindSpeed * 1.2 : baseWindSpeed * 0.8;
  const fallWindSpeed = baseWindSpeed;

  const maxWindSpeed =
    Math.max(winterWindSpeed, springWindSpeed, summerWindSpeed, fallWindSpeed) *
    1.5;
  const minWindSpeed =
    Math.min(winterWindSpeed, springWindSpeed, summerWindSpeed, fallWindSpeed) *
    0.7;
  const windRange = maxWindSpeed - minWindSpeed;

  // Calculate comfortable wind days (2-6 m/s is generally considered comfortable for outdoor activities)
  const comfortableWindDays = 365 * 0.6; // Estimated based on typical wind patterns
  const comfortablePercentage = (comfortableWindDays / 365) * 100;

  // Generate synthetic wind data for graph
  const generateWindData = () => {
    const data = [];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const seasonalMultipliers = [
      1.1, 1.0, 1.1, 1.2, 1.0, 0.8, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2,
    ];

    for (let i = 0; i < 12; i++) {
      const monthlyWind =
        baseWindSpeed * seasonalMultipliers[i] * (0.8 + Math.random() * 0.4);
      data.push({
        month: months[i],
        windSpeed: monthlyWind,
        dayOfYear: (i + 1) * 30,
        originalIndex: i,
      });
    }
    return data;
  };

  const windData = generateWindData();

  return (
    <div
      id="wind-analysis-section"
      className="min-h-screen bg-gray-100 px-14 py-8 pt-28"
    >
      {/* Wind Heading */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 tracking-wide">
          Wind & Ventilation
        </h1>
      </div>

      {/* Three Cards Layout - Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* MACRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem]">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MACRO
            </h3>
          </div>

          {/* Top section - wind metrics */}
          <div className="flex justify-between items-start mb-8">
            {/* Left side - Max and Min wind speed */}
            <div className="flex-1">
              <div className="mb-4">
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Maximum Wind Speed
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {maxWindSpeed.toFixed(1)} m/s
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Minimum Wind Speed
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {minWindSpeed.toFixed(1)} m/s
                </div>
              </div>
            </div>

            {/* Right side - Annual average wind speed */}
            <div className="flex-1 text-right">
              <div className="text-5xl font-black text-black mb-2">
                {annualAvgWindSpeed.toFixed(1)}
              </div>
              <div className="text-sm text-gray-500 font-bold">
                m/s Annual Avg.
              </div>
            </div>
          </div>

          {/* Bottom section - Classification information */}
          <div>
            {/* Large Köppen classification code */}
            <div
              className="text-6xl font-black leading-none mb-4"
              style={{
                color: getDetailedClimateColor(
                  climateInfo.mainClass,
                  climateInfo.subClass
                ),
              }}
            >
              {climateInfo.mainClass}
              {climateInfo.subClass}
            </div>

            {/* Classification label */}
            <div className="text-sm text-gray-500 font-bold mb-4">
              Köppen-Geiger Climate Classification
            </div>

            {/* Climate name */}
            <div className="font-bold text-gray-800 mb-4 text-lg">
              {climateInfo.fullName}
            </div>

            {/* Description */}
            <div className="text-sm text-gray-500 leading-relaxed font-bold">
              <div>
                {annualAvgWindSpeed >= 6
                  ? "Strong prevailing winds provide excellent natural ventilation potential throughout the year."
                  : annualAvgWindSpeed >= 4
                  ? "Moderate wind patterns offer good opportunities for natural ventilation and cooling strategies."
                  : annualAvgWindSpeed >= 2
                  ? "Light to moderate winds require strategic building orientation to maximize natural ventilation benefits."
                  : "Calm conditions with minimal wind necessitate mechanical ventilation systems and stack effect strategies."}
              </div>
            </div>
          </div>
        </div>

        {/* MICRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MICRO
            </h3>
          </div>

          {/* Wind Range at top */}
          <div className="text-left mb-8">
            <div className="text-5xl font-black text-black mb-2">
              {windRange.toFixed(1)}
            </div>
            <div className="text-sm text-gray-500 font-bold">
              m/s Wind Range
            </div>
          </div>

          {/* Seasonal Wind Patterns */}
          <div className="mb-8 flex-grow">
            <h4 className="font-bold text-gray-500 mb-4 text-lg">
              Seasonal Patterns
            </h4>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Winter:</div>
                  <div className="font-bold text-gray-500">
                    Avg: {winterWindSpeed.toFixed(1)} m/s
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Spring:</div>
                  <div className="font-bold text-gray-500">
                    Avg: {springWindSpeed.toFixed(1)} m/s
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Summer:</div>
                  <div className="font-bold text-gray-500">
                    Avg: {summerWindSpeed.toFixed(1)} m/s
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Fall:</div>
                  <div className="font-bold text-gray-500">
                    Avg: {fallWindSpeed.toFixed(1)} m/s
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wind Extremes */}
          <div className="flex justify-between items-center mt-auto">
            <div className="text-left">
              <div className="text-4xl font-black text-black">
                {minWindSpeed.toFixed(1)}
              </div>
              <div className="text-sm text-gray-500 font-bold">
                m/s calm conditions
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-black">
                {maxWindSpeed.toFixed(1)}
              </div>
              <div className="text-sm text-gray-500 font-bold">
                m/s peak winds
              </div>
            </div>
          </div>
        </div>

        {/* DESIGN Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              DESIGN
            </h3>
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white rounded-sm"></div>
            </div>
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 bg-white transform rotate-45"></div>
            </div>
          </div>

          <div className="mb-4 flex-grow">
            <h4 className="font-bold text-gray-800 mb-2">
              Ventilation & Airflow Design Implications
            </h4>

            {/* Conditional content: Show loading, AI suggestions, error, or static text */}
            {loadingDesignSuggestions ? (
              <div className="text-sm text-gray-700 space-y-2">
                <style jsx>{`
                  @keyframes windWave1 {
                    0%,
                    100% {
                      background-color: #d1d5db;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  @keyframes windWave2 {
                    0%,
                    100% {
                      background-color: #9ca3af;
                    }
                    50% {
                      background-color: #d1d5db;
                    }
                  }
                  @keyframes windWave3 {
                    0%,
                    100% {
                      background-color: #e5e7eb;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  .animate-wind-wave-1 {
                    animation: windWave1 2s ease-in-out infinite;
                  }
                  .animate-wind-wave-2 {
                    animation: windWave2 2s ease-in-out infinite 0.3s;
                  }
                  .animate-wind-wave-3 {
                    animation: windWave3 2s ease-in-out infinite 0.6s;
                  }
                `}</style>
                <div className="h-3 rounded animate-wind-wave-1"></div>
                <div className="h-3 rounded animate-wind-wave-2 w-4/5"></div>
                <div className="h-3 rounded animate-wind-wave-3 w-3/5"></div>
                <div className="h-3 rounded animate-wind-wave-1 w-5/6"></div>
                <div className="h-3 rounded animate-wind-wave-2 w-2/3"></div>
                <div className="h-3 rounded animate-wind-wave-3"></div>
              </div>
            ) : designSuggestions ? (
              <div className="text-sm text-gray-500 leading-relaxed font-bold whitespace-pre-wrap">
                {designSuggestions}
              </div>
            ) : designError ? (
              <div className="text-red-600 text-sm font-bold">
                Error: {designError}
              </div>
            ) : (
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  This location has{" "}
                  {annualAvgWindSpeed >= 7
                    ? "strong winds ideal for natural ventilation but requiring careful opening design and wind protection strategies"
                    : annualAvgWindSpeed >= 4
                    ? "moderate winds suitable for effective cross-ventilation and stack effect cooling strategies"
                    : annualAvgWindSpeed >= 2
                    ? "light winds requiring enhanced stack ventilation and larger openings for effective natural airflow"
                    : "very light winds necessitating mechanical ventilation assistance and optimized building orientation"}{" "}
                  with {annualAvgWindSpeed.toFixed(1)} m/s average wind speeds.
                </p>
                <p>
                  The {windRange.toFixed(1)} m/s seasonal wind variation{" "}
                  {windRange > 3
                    ? "requires variable opening controls and adaptive ventilation strategies throughout the year"
                    : "allows for consistent natural ventilation design approaches"}{" "}
                  with {comfortablePercentage.toFixed(1)}% of days having
                  comfortable wind conditions for natural ventilation.
                </p>
              </div>
            )}

            {/* Design Suggestion Text and AI Button */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-black text-sm font-bold">
                Design Suggestion
              </span>
              <button
                className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
                onClick={async () => {
                  if (!OPENROUTER_API_KEY) {
                    setDesignError("OpenRouter API key not configured");
                    return;
                  }

                  setDesignSuggestions(null);
                  setDesignError(null);
                  setLoadingDesignSuggestions(true);

                  try {
                    const response = await fetch(
                      "https://openrouter.ai/api/v1/chat/completions",
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                          "HTTP-Referer": window.location.origin,
                          "X-Title": "ClimaPro.AI",
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          model: "anthropic/claude-3.7-sonnet",
                          route: "fallback",
                          messages: [
                            {
                              role: "system",
                              content:
                                "You are an expert building design consultant specializing in natural ventilation, airflow design, and wind analysis in architecture. Analyze the wind and ventilation metrics provided to give location-specific building design recommendations. Structure your response as exactly 3 paragraphs: 1) Natural ventilation strategy, 2) Building orientation approach, 3) Opening design and controls. Include specific wind speed numbers in your reasoning. Keep total response under 75 words with actionable, ventilation-focused design advice.",
                            },
                            {
                              role: "user",
                              content: `For this ${climateInfo.mainClass}${
                                climateInfo.subClass
                              } (${
                                climateInfo.fullName
                              }) climate location, provide targeted building design recommendations based on these wind metrics:

WIND & VENTILATION PROFILE:
- Annual Average Wind Speed: ${annualAvgWindSpeed.toFixed(1)} m/s
- Maximum Wind Speed: ${maxWindSpeed.toFixed(1)} m/s
- Minimum Wind Speed: ${minWindSpeed.toFixed(1)} m/s
- Wind Range: ${windRange.toFixed(1)} m/s
- Winter Winds: ${winterWindSpeed.toFixed(1)} m/s
- Summer Winds: ${summerWindSpeed.toFixed(1)} m/s
- Comfortable Wind Days: ${comfortablePercentage.toFixed(1)}%
- Temperature Range: ${tempRange.toFixed(1)}°C

Based on this wind and climate data, provide design recommendations with clear reasoning:

1. NATURAL VENTILATION: Explain cross-ventilation and stack effect strategies based on the ${annualAvgWindSpeed.toFixed(
                                1
                              )} m/s average wind speed and ${windRange.toFixed(
                                1
                              )} m/s seasonal variation. Reference these numbers in your reasoning.

2. BUILDING ORIENTATION: Justify building positioning and form based on the ${maxWindSpeed.toFixed(
                                1
                              )} m/s maximum winds and ${minWindSpeed.toFixed(
                                1
                              )} m/s minimum conditions. Use these specific values in your explanation.

3. OPENING DESIGN: Recommend window sizing, placement, and controls based on the ${winterWindSpeed.toFixed(
                                1
                              )} m/s winter and ${summerWindSpeed.toFixed(
                                1
                              )} m/s summer wind patterns. Explain why these specific wind numbers drive your recommendations.

Use the actual wind values (${annualAvgWindSpeed.toFixed(
                                1
                              )} m/s avg, ${windRange.toFixed(
                                1
                              )} m/s range, ${comfortablePercentage.toFixed(
                                1
                              )}% comfort days) to justify each design decision.`,
                            },
                          ],
                        }),
                      }
                    );

                    if (!response.ok) {
                      if (response.status === 503) {
                        throw new Error("AI service temporarily unavailable");
                      } else if (response.status === 401) {
                        throw new Error("Authentication failed");
                      } else if (response.status === 429) {
                        throw new Error("Rate limit exceeded");
                      }
                      throw new Error(`Request failed: ${response.status}`);
                    }

                    const data = await response.json();

                    if (
                      data.choices &&
                      data.choices[0] &&
                      data.choices[0].message
                    ) {
                      setDesignSuggestions(data.choices[0].message.content);
                    } else if (data.error) {
                      throw new Error(data.error.message || "Unknown AI error");
                    }
                  } catch (error) {
                    console.error("AI wind suggestions error:", error);
                    setDesignError(error.message);
                  } finally {
                    setLoadingDesignSuggestions(false);
                  }
                }}
                disabled={loadingDesignSuggestions}
              >
                {loadingDesignSuggestions ? "..." : "ClimaPro.AI"}
              </button>
            </div>
          </div>

          <div className="flex items-end justify-between mt-auto">
            <div>
              <div className="text-sm text-gray-600 mb-1">+ VENTILATION</div>
              <div className="text-sm text-gray-600">+ AIRFLOW</div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-gray-900">
                {comfortablePercentage.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Comfortable Days
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wind Graph Card - Bottom Section */}
      <div className="bg-gray-100 border border-gray-500 rounded-[2rem] overflow-hidden">
        <button
          className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-200 transition-colors"
          onClick={() => {
            const graphContent = document.getElementById("wind-graph-content");
            const chevron = document.getElementById("wind-graph-chevron");
            if (
              graphContent.style.display === "none" ||
              !graphContent.style.display
            ) {
              graphContent.style.display = "block";
              chevron.style.transform = "rotate(180deg)";
            } else {
              graphContent.style.display = "none";
              chevron.style.transform = "rotate(0deg)";
            }
          }}
        >
          <div className="flex items-center space-x-4 ml-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              GRAPH DATA
            </h3>
            <button
              className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
              onClick={async (e) => {
                e.stopPropagation();

                if (!OPENROUTER_API_KEY) {
                  setAiError("OpenRouter API key not configured");
                  return;
                }

                // Clear previous results before starting new analysis
                setAiInterpretation(null);
                setAiError(null);
                setLoadingAiInterpretation(true);

                try {
                  // Calculate wind statistics
                  const windStats = {
                    annualAvg: annualAvgWindSpeed.toFixed(1),
                    maxWindSpeed: maxWindSpeed.toFixed(1),
                    minWindSpeed: minWindSpeed.toFixed(1),
                    windRange: windRange.toFixed(1),
                    winterAvg: winterWindSpeed.toFixed(1),
                    springAvg: springWindSpeed.toFixed(1),
                    summerAvg: summerWindSpeed.toFixed(1),
                    fallAvg: fallWindSpeed.toFixed(1),
                  };

                  const response = await fetch(
                    "https://openrouter.ai/api/v1/chat/completions",
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                        "HTTP-Referer": window.location.origin,
                        "X-Title": "ClimaPro.AI",
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        model: "anthropic/claude-3.7-sonnet",
                        route: "fallback",
                        messages: [
                          {
                            role: "system",
                            content:
                              "You are an expert climate data analyst specializing in wind pattern and airflow graph interpretation. Focus exclusively on analyzing wind speed patterns, seasonal variations, and ventilation potential trends. Provide insights specifically about what the wind graph reveals, not general climate advice. Keep responses concise, 100 words, focusing on graph-specific observations.",
                          },
                          {
                            role: "user",
                            content: `Analyze the wind speed and ventilation graph patterns for this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} (${
                              climateInfo.fullName
                            }) climate location:

ANNUAL STATISTICS:
- Annual Average Wind Speed: ${windStats.annualAvg} m/s
- Maximum Wind Speed: ${windStats.maxWindSpeed} m/s
- Minimum Wind Speed: ${windStats.minWindSpeed} m/s
- Total Wind Range: ${windStats.windRange} m/s

SEASONAL PATTERNS:
- Winter (Dec-Feb): ${windStats.winterAvg} m/s average
- Spring (Mar-May): ${windStats.springAvg} m/s average
- Summer (Jun-Aug): ${windStats.summerAvg} m/s average
- Fall (Sep-Nov): ${windStats.fallAvg} m/s average

SEASONAL WIND RANGES:
- Winter-Summer difference: ${(
                              parseFloat(windStats.winterAvg) -
                              parseFloat(windStats.summerAvg)
                            ).toFixed(1)} m/s
- Spring-Fall difference: ${(
                              parseFloat(windStats.springAvg) -
                              parseFloat(windStats.fallAvg)
                            ).toFixed(1)} m/s

Analyze specifically what this wind speed graph reveals about:
1. Seasonal wind transition patterns and consistency
2. Wind speed variability and natural ventilation potential within this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} classification
3. Peak and calm period timing patterns visible in the graph
4. Graph trend characteristics that define this climate's airflow patterns
5. Notable wind speed extremes or ventilation opportunities shown in the data

Focus only on what the wind speed graph data and patterns show, not design recommendations.`,
                          },
                        ],
                      }),
                    }
                  );

                  if (!response.ok) {
                    if (response.status === 503) {
                      throw new Error("AI service temporarily unavailable");
                    } else if (response.status === 401) {
                      throw new Error("Authentication failed");
                    } else if (response.status === 429) {
                      throw new Error("Rate limit exceeded");
                    }
                    throw new Error(`Request failed: ${response.status}`);
                  }

                  const data = await response.json();

                  if (
                    data.choices &&
                    data.choices[0] &&
                    data.choices[0].message
                  ) {
                    setAiInterpretation(data.choices[0].message.content);
                  } else if (data.error) {
                    throw new Error(data.error.message || "Unknown AI error");
                  }
                } catch (error) {
                  console.error("AI wind interpretation error:", error);
                  setAiError(error.message);
                } finally {
                  setLoadingAiInterpretation(false);
                }
              }}
              disabled={loadingAiInterpretation}
            >
              {loadingAiInterpretation ? "..." : "ClimaPro.AI"}
            </button>
          </div>
          <svg
            id="wind-graph-chevron"
            className="w-8 h-8 text-gray-600 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div
          id="wind-graph-content"
          style={{ display: "none" }}
          className="px-8 pb-8"
        >
          <>
            {/* Conditional Layout: Split when AI content exists, full width otherwise */}
            {aiInterpretation || aiError || loadingAiInterpretation ? (
              <div className="flex gap-6">
                {/* AI Interpretation Section - Left 30% */}
                <div className="w-[30%] bg-gray-100">
                  <div className="h-full">
                    <h4 className="font-bold text-gray-800 mb-4">
                      AI Wind Analysis
                    </h4>
                    {loadingAiInterpretation && (
                      <div className="space-y-3">
                        {/* Skeleton loading rectangles with color animation */}
                        <style jsx>{`
                          @keyframes windColorWave1 {
                            0%,
                            100% {
                              background-color: #d1d5db;
                            }
                            50% {
                              background-color: #9ca3af;
                            }
                          }
                          @keyframes windColorWave2 {
                            0%,
                            100% {
                              background-color: #9ca3af;
                            }
                            50% {
                              background-color: #d1d5db;
                            }
                          }
                          @keyframes windColorWave3 {
                            0%,
                            100% {
                              background-color: #e5e7eb;
                            }
                            50% {
                              background-color: #9ca3af;
                            }
                          }
                          .animate-wind-wave-1 {
                            animation: windColorWave1 2s ease-in-out infinite;
                          }
                          .animate-wind-wave-2 {
                            animation: windColorWave2 2s ease-in-out infinite
                              0.3s;
                          }
                          .animate-wind-wave-3 {
                            animation: windColorWave3 2s ease-in-out infinite
                              0.6s;
                          }
                        `}</style>
                        <div className="h-3 rounded animate-wind-wave-1"></div>
                        <div className="h-3 rounded animate-wind-wave-2 w-4/5"></div>
                        <div className="h-3 rounded animate-wind-wave-3 w-3/5"></div>
                        <div className="h-3 rounded animate-wind-wave-1 w-5/6"></div>
                        <div className="h-3 rounded animate-wind-wave-2 w-2/3"></div>
                        <div className="h-3 rounded animate-wind-wave-3"></div>
                        <div className="h-3 rounded animate-wind-wave-1 w-3/4"></div>
                        <div className="h-3 rounded animate-wind-wave-2 w-4/5"></div>
                        <div className="h-3 rounded animate-wind-wave-3 w-1/2"></div>
                        <div className="h-3 rounded animate-wind-wave-1 w-3/5"></div>
                        <div className="h-3 rounded animate-wind-wave-2 w-5/6"></div>
                        <div className="h-3 rounded animate-wind-wave-3 w-2/3"></div>
                      </div>
                    )}
                    {aiError && (
                      <div className="text-red-600 text-sm font-bold">
                        Error: {aiError}
                      </div>
                    )}
                    {aiInterpretation && (
                      <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap font-bold">
                        {aiInterpretation}
                      </div>
                    )}
                  </div>
                </div>

                {/* Graph Section - Right 70% when AI content exists */}
                <div className="w-[70%] h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={windData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          label={{
                            value: "m/s",
                            angle: -90,
                            position: "insideLeft",
                          }}
                          domain={[0, "dataMax + 2"]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                          formatter={(value) => [
                            `${value.toFixed(1)} m/s`,
                            "Wind Speed",
                          ]}
                        />

                        <ReferenceLine
                          y={2}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />
                        <ReferenceLine
                          y={6}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />

                        <Line
                          type="monotone"
                          dataKey="windSpeed"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={false}
                          name="windSpeed"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex justify-center space-x-8 mt-4 text-sm">
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-emerald-500 mr-3"></div>
                      <span className="text-gray-700">Wind Speed</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                      <span className="text-gray-700">
                        Comfort Range (2-6 m/s)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Full Width Graph - When no AI content */
              <div className="h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={windData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#666" }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#666" }}
                        label={{
                          value: "m/s",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        domain={[0, "dataMax + 2"]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "8px",
                        }}
                        formatter={(value) => [
                          `${value.toFixed(1)} m/s`,
                          "Wind Speed",
                        ]}
                      />

                      <ReferenceLine
                        y={2}
                        stroke="#e0e0e0"
                        strokeDasharray="2 2"
                      />
                      <ReferenceLine
                        y={6}
                        stroke="#e0e0e0"
                        strokeDasharray="2 2"
                      />

                      <Line
                        type="monotone"
                        dataKey="windSpeed"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="windSpeed"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex justify-center space-x-8 mt-4 text-sm">
                  <div className="flex items-center">
                    <div className="w-6 h-0.5 bg-emerald-500 mr-3"></div>
                    <span className="text-gray-700">Wind Speed</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                    <span className="text-gray-700">
                      Comfort Range (2-6 m/s)
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default WindAnalysis;
